/**
 * ProposalsList (Mix Plans tab) — [REFACTOR-2026-06-06]
 *
 * A Mix Plan is now a read-only segment-SELECTION proposal. This tab shows the
 * generator plus a list of proposal cards (title / brief / segment count /
 * approx duration / purpose chips). Each card has a "精修此方案" button that
 * creates a Render draft (entering the polish workbench). A plan can be turned
 * into many renders — the button can be pressed repeatedly.
 */
import { useState } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { ChevronDown, ChevronUp, Eye, Film, Loader2, Scissors, Sparkles, Trash2 } from 'lucide-react'

import { dramaApi, dramaQueryKeys, pickLocalized } from '@/lib/api/drama'
import type { DramaEditPlan, DramaLang, DramaSegment } from '@/types/drama'

import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { toast } from '@/components/ui/toaster'

import { GenerateMixForm } from './GenerateMixForm'
import { AuthedVideo } from './AuthedVideo'
import { dramaT } from './i18n'

interface Props {
  projectId: string
  plans: DramaEditPlan[]
  segments: DramaSegment[]
  lang: DramaLang
  onEnterWorkbench: (renderCode: string) => void
}

function approxDuration(plan: DramaEditPlan, segMap: Map<string, DramaSegment>): number {
  return (plan.plan_json.timeline || []).reduce((sum, it) => {
    const seg = segMap.get(it.segment_code)
    const segDur = seg ? seg.end_seconds - seg.start_seconds : 0
    const end = it.source_end_offset ?? segDur
    return sum + Math.max(0, end - (it.source_start_offset || 0))
  }, 0)
}

export function ProposalsList({ projectId, plans, segments, lang, onEnterWorkbench }: Props) {
  const t = dramaT(lang)
  const queryClient = useQueryClient()
  const segMap = new Map(segments.map((s) => [s.segment_code, s]))
  const [generatorOpen, setGeneratorOpen] = useState(plans.length === 0)
  const [viewPlan, setViewPlan] = useState<DramaEditPlan | null>(null)
  const [previewOrders, setPreviewOrders] = useState<Set<number>>(new Set())

  const togglePreview = (order: number) =>
    setPreviewOrders((prev) => {
      const next = new Set(prev)
      next.has(order) ? next.delete(order) : next.add(order)
      return next
    })

  const openView = (p: DramaEditPlan) => {
    setPreviewOrders(new Set())
    setViewPlan(p)
  }

  const deleteMutation = useMutation({
    mutationFn: (planCode: string) => dramaApi.deleteEditPlan(projectId, planCode),
    onSuccess: (data) => {
      toast({ title: t('toast.planDeleted'), description: data.plan_code })
      queryClient.invalidateQueries({ queryKey: dramaQueryKeys.detail(projectId) })
    },
    onError: (e: any) =>
      toast({
        title: t('toast.deleteFailed'),
        description: e?.response?.data?.detail || e.message,
        variant: 'destructive',
      }),
  })

  const draftMutation = useMutation({
    mutationFn: (planCode: string) => dramaApi.createRenderDraft(projectId, planCode),
    onSuccess: (data) => {
      toast({ title: t('toast.draftCreated'), description: data.render_code })
      queryClient.invalidateQueries({ queryKey: dramaQueryKeys.detail(projectId) })
      onEnterWorkbench(data.render_code)
    },
    onError: (e: any) =>
      toast({
        title: t('toast.createFailed'),
        description: e?.response?.data?.detail || e.message,
        variant: 'destructive',
      }),
  })

  return (
    <div className="space-y-4">
      {/* Collapsible generator */}
      <div className="border rounded-lg bg-card">
        {generatorOpen ? (
          <div className="p-4">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-sm font-semibold flex items-center gap-2">
                <Sparkles className="w-4 h-4 text-spectrum-magenta" /> {t('plans.generateNew')}
              </h3>
              {plans.length > 0 && (
                <Button size="sm" variant="ghost" onClick={() => setGeneratorOpen(false)}>
                  <ChevronUp className="w-3.5 h-3.5 mr-1" /> {t('plans.collapse')}
                </Button>
              )}
            </div>
            <GenerateMixForm
              projectId={projectId}
              lang={lang}
              onComplete={() => setGeneratorOpen(false)}
            />
          </div>
        ) : (
          <button
            type="button"
            onClick={() => setGeneratorOpen(true)}
            className="w-full p-3 flex items-center justify-center gap-2 text-sm text-muted-foreground hover:text-foreground hover:bg-accent/30 transition-colors"
          >
            <ChevronDown className="w-4 h-4" />
            <Sparkles className="w-4 h-4 text-spectrum-magenta" />
            {t('plans.generateMore')}
          </button>
        )}
      </div>

      {plans.length === 0 ? (
        <div className="text-center py-8 border-2 border-dashed rounded-lg">
          <Film className="w-10 h-10 mx-auto text-muted-foreground mb-2" />
          <p className="text-sm text-muted-foreground">{t('plans.emptyTitle')}</p>
        </div>
      ) : (
        <div className="grid gap-3 md:grid-cols-2">
          {plans.map((p) => {
            const timeline = p.plan_json.timeline || []
            const dur = approxDuration(p, segMap)
            const target = p.plan_json.target_duration_seconds
            return (
              <div key={p.plan_code} className="p-4 border rounded-lg bg-card flex flex-col">
                <div className="flex items-center justify-between mb-1">
                  <code className="text-xs text-muted-foreground">{p.plan_code}</code>
                  <Badge variant="outline">{t('plans.segs', timeline.length)}</Badge>
                </div>
                <div className="font-medium text-sm mb-1 line-clamp-1">
                  {p.title || t('plans.untitled')}
                </div>
                <div className="text-xs text-muted-foreground line-clamp-2 mb-2">
                  {p.user_brief}
                </div>
                <div className="flex flex-wrap gap-1 mb-3">
                  {timeline.slice(0, 8).map((it) => (
                    <span
                      key={it.order}
                      className="text-[10px] px-1.5 py-0.5 rounded bg-muted text-muted-foreground"
                    >
                      {it.purpose}
                    </span>
                  ))}
                </div>
                <div className="mt-auto flex items-center justify-between gap-2">
                  <span className="text-xs text-muted-foreground shrink-0">
                    {t('plans.approx', Math.round(dur), target)}
                  </span>
                  <div className="flex items-center gap-1">
                    <Button size="sm" variant="ghost" onClick={() => openView(p)}>
                      <Eye className="w-3.5 h-3.5 mr-1" /> {t('plans.view')}
                    </Button>
                    <Button
                      size="sm"
                      onClick={() => draftMutation.mutate(p.plan_code)}
                      disabled={draftMutation.isPending}
                    >
                      {draftMutation.isPending && draftMutation.variables === p.plan_code ? (
                        <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" />
                      ) : (
                        <Scissors className="w-3.5 h-3.5 mr-1" />
                      )}
                      {t('plans.edit')}
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      className="text-destructive"
                      onClick={() => {
                        if (confirm(t('plans.deleteConfirm', p.plan_code))) {
                          deleteMutation.mutate(p.plan_code)
                        }
                      }}
                      disabled={deleteMutation.isPending && deleteMutation.variables === p.plan_code}
                    >
                      {deleteMutation.isPending && deleteMutation.variables === p.plan_code ? (
                        <Loader2 className="w-3.5 h-3.5 animate-spin" />
                      ) : (
                        <Trash2 className="w-3.5 h-3.5" />
                      )}
                    </Button>
                  </div>
                </div>
              </div>
            )
          })}
        </div>
      )}

      {/* Proposal detail dialog */}
      <Dialog open={!!viewPlan} onOpenChange={(o) => !o && setViewPlan(null)}>
        <DialogContent className="sm:max-w-2xl">
          <DialogHeader>
            <DialogTitle>
              {viewPlan?.title || '未命名方案'}{' '}
              <code className="text-xs text-muted-foreground">{viewPlan?.plan_code}</code>
            </DialogTitle>
            {/* Full brief — show complete content, wraps, no scrollbar. */}
            <DialogDescription asChild>
              <div className="whitespace-pre-line text-sm">
                {viewPlan?.user_brief}
              </div>
            </DialogDescription>
          </DialogHeader>
          {viewPlan && (
            <div className="space-y-3">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <div className="flex flex-wrap gap-2 text-[11px]">
                  <Badge variant="outline">{t('plans.segs', (viewPlan.plan_json.timeline || []).length)}</Badge>
                  <Badge variant="outline">
                    {t('plans.approx', Math.round(approxDuration(viewPlan, segMap)), viewPlan.plan_json.target_duration_seconds)}
                  </Badge>
                </div>
              </div>
              <div className="max-h-[55vh] overflow-y-auto space-y-1.5 pr-1">
                {(viewPlan.plan_json.timeline || []).map((it) => {
                  const seg = segMap.get(it.segment_code)
                  const segDur = seg ? seg.end_seconds - seg.start_seconds : 0
                  const end = it.source_end_offset ?? segDur
                  const d = Math.max(0, end - (it.source_start_offset || 0))
                  const summary = seg
                    ? pickLocalized(seg.metadata?.summary_i18n, lang) || seg.summary || ''
                    : ''
                  const open = previewOrders.has(it.order)
                  return (
                    <div key={it.order} className="p-2 rounded-md border text-xs">
                      <div className="flex items-start gap-2">
                        <span className="font-mono text-muted-foreground shrink-0">#{it.order}</span>
                        <div className="min-w-0 flex-1">
                          <div className="flex flex-wrap items-center gap-1.5">
                            <Badge variant="secondary">{it.purpose}</Badge>
                            {it.segment_code ? (
                              <code className="text-[10px]">{it.segment_code}</code>
                            ) : (
                              <span className="text-muted-foreground">{t('plans.tailPlaceholder')}</span>
                            )}
                            {it.episode_number != null && <span className="text-muted-foreground">EP {it.episode_number}</span>}
                            <span className="text-muted-foreground">
                              {(it.source_start_offset || 0).toFixed(1)}–{end ? end.toFixed(1) : '末'}s · {d.toFixed(1)}s
                            </span>
                          </div>
                          {summary && <div className="mt-1 text-muted-foreground">{summary}</div>}
                        </div>
                        {it.segment_code && (
                          <Button
                            size="sm"
                            variant="ghost"
                            className="h-7 shrink-0"
                            onClick={() => togglePreview(it.order)}
                          >
                            <Film className="w-3.5 h-3.5 mr-1" />
                            {open ? t('plans.previewClose') : t('plans.previewOpen')}
                          </Button>
                        )}
                      </div>
                      {open && it.segment_code && (
                        <AuthedVideo
                          path={dramaApi.segmentFileUrl(projectId, it.segment_code)}
                          stream
                          controls
                          autoPlay
                          className="w-full mt-2 rounded-md bg-black aspect-[9/16] max-h-[50vh]"
                        />
                      )}
                    </div>
                  )
                })}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  )
}
